import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { fetchUserShowcase, createSectionAsync, createPageAsync, updateSection, updatePage, updateSectionAsync, updatePageAsync, deleteSectionAsync, deletePageAsync } from '../../../store/slices/showcaseSlice';
import { PlusIcon, ChevronRightIcon, ChevronLeftIcon, PhotoIcon, SquaresPlusIcon, ArrowsUpDownIcon, TrashIcon } from '@heroicons/react/24/outline';
import ShowcaseGrid from './ShowcaseGrid';
import ShowcaseEditor from './ShowcaseEditor';
import Button from '../../common/Button';
import PromptModal from '../../common/PromptModal';
import ConfirmationModal from '../../common/ConfirmationModal';
import ReorderModal from './ReorderModal';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { ShowcaseSection, ShowcasePage } from '../../../types';
import { getShowcaseImageUrl } from '../../../utils/showcase';
import { useRestrictions } from '../../../hooks/useRestrictions';
// [Subscription removed] UpgradePopup removed — showcase sections are unlimited.
// import UpgradePopup from '../../common/UpgradePopup';

interface ShowcaseTabProps {
  userId: string;
  isOwnProfile: boolean;
}

const ShowcaseTab: React.FC<ShowcaseTabProps> = ({ userId, isOwnProfile }) => {
  const dispatch = useAppDispatch();
  const { sections, loading, error } = useAppSelector((state) => state.showcase);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);

  // Modal States
  const [showSectionPrompt, setShowSectionPrompt] = useState(false);
  const [showPagePrompt, setShowPagePrompt] = useState(false);
  const [showReorderSections, setShowReorderSections] = useState(false);
  const [showReorderPages, setShowReorderPages] = useState(false);
  const [showDeleteSectionModal, setShowDeleteSectionModal] = useState(false);
  const [showDeletePageModal, setShowDeletePageModal] = useState(false);
  const [draggedSectionId, setDraggedSectionId] = useState<string | null>(null);
  const [draggedPageIndex, setDraggedPageIndex] = useState<number | null>(null);

  // [Subscription removed] Showcase sections are unrestricted — platform is fully free.
  // const { checkRestriction } = useRestrictions();

  useEffect(() => {
    dispatch(fetchUserShowcase(userId));
  }, [dispatch, userId]);

  useEffect(() => {
    if (sections.length > 0 && !activeSectionId) {
      setActiveSectionId(sections[0].id);
    }
  }, [sections, activeSectionId]);

  const activeSection = sections.find(s => s.id === activeSectionId);
  const activePage = activeSection?.pages[activePageIndex];

  const handleAddSection = async (title: string) => {
    const result = await dispatch(createSectionAsync({ title }));
    if (createSectionAsync.fulfilled.match(result)) {
      setActiveSectionId(result.payload.id);
      setActivePageIndex(0);
    }
  };

  const handleAddPage = async (title: string) => {
    if (!activeSectionId) return;
    const result = await dispatch(createPageAsync({ 
      sectionId: activeSectionId, 
      title, 
      layoutType: 'grid' 
    }));
    if (createPageAsync.fulfilled.match(result)) {
      setActivePageIndex(activeSection?.pages.length || 0);
    }
  };

  const handleSectionDragStart = (e: React.DragEvent, sectionId: string) => {
    setDraggedSectionId(sectionId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSectionDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleSectionDrop = (e: React.DragEvent, targetSectionId: string) => {
    e.preventDefault();
    if (!draggedSectionId || draggedSectionId === targetSectionId) return;

    const draggedIndex = sections.findIndex(s => s.id === draggedSectionId);
    const targetIndex = sections.findIndex(s => s.id === targetSectionId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Reorder sections
    const newSections = [...sections];
    const [removed] = newSections.splice(draggedIndex, 1);
    newSections.splice(targetIndex, 0, removed);

    // Update order values and persist to backend
    newSections.forEach((section, index) => {
      dispatch(updateSectionAsync({ ...section, order: index }));
    });

    setDraggedSectionId(null);
  };

  const handlePageDragStart = (e: React.DragEvent, pageIndex: number) => {
    setDraggedPageIndex(pageIndex);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handlePageDrop = (e: React.DragEvent, targetPageIndex: number) => {
    e.preventDefault();
    if (draggedPageIndex === null || draggedPageIndex === targetPageIndex || !activeSection) return;

    const newPages = [...activeSection.pages];
    const [removed] = newPages.splice(draggedPageIndex, 1);
    newPages.splice(targetPageIndex, 0, removed);

    // Update order values and persist to backend
    newPages.forEach((page, index) => {
      dispatch(updatePageAsync({ sectionId: activeSectionId!, page: { ...page, order: index } }));
    });

    setDraggedPageIndex(null);
    setActivePageIndex(targetPageIndex);
  };

  const handleConfirmDeleteSection = async () => {
    if (!activeSectionId) return;
    
    const result = await dispatch(deleteSectionAsync(activeSectionId));
    if (deleteSectionAsync.fulfilled.match(result)) {
      // If successful, the section is removed from state.
      // We need to update activeSectionId to the first available section or null.
      const remainingSections = sections.filter(s => s.id !== activeSectionId);
      if (remainingSections.length > 0) {
        setActiveSectionId(remainingSections[0].id);
      } else {
        setActiveSectionId(null);
      }
      setActivePageIndex(0);
      setShowDeleteSectionModal(false);
    }
  };

  const handleConfirmDeletePage = async () => {
    if (!activeSectionId || !activePage) return;
    
    const result = await dispatch(deletePageAsync({ sectionId: activeSectionId, pageId: activePage.id }));
    if (deletePageAsync.fulfilled.match(result)) {
      // Update activePageIndex if needed
      if (activePageIndex > 0) {
        setActivePageIndex(prev => prev - 1);
      } else {
        setActivePageIndex(0);
      }
      setShowDeletePageModal(false);
    }
  };

  if (loading && sections.length === 0) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <>
        <div className="text-center py-20 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border-2 border-dashed border-gray-200 dark:border-gray-700 mx-4 md:mx-0">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-sm">
            <PhotoIcon className="h-8 w-8 text-gray-300 dark:text-gray-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">No Showcase Collections</h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-xs mx-auto">Create artistic grids to showcase your work in unique arrangements.</p>
          {isOwnProfile && (
            <Button 
              variant="primary" 
              onClick={() => {
                 // [Subscription removed] Showcase section creation is unrestricted
                 setShowSectionPrompt(true);
              }}
              className="flex items-center gap-2 mx-auto shadow-lg shadow-black/20"
            >
              <PlusIcon className="h-5 w-5" />
              Create Your First Showcase
            </Button>
          )}
        </div>

        {/* Modals */}
        <PromptModal
          isOpen={showSectionPrompt}
          onClose={() => setShowSectionPrompt(false)}
          onConfirm={handleAddSection}
          title="New Collection"
          message="Give your new showcase collection a name (e.g., 'Portrait Series', 'Street Photography')"
          placeholder="Collection title..."
        />
      </>
    );
  }

  return (
    <div className="space-y-8 mx-4 md:mx-0">
      {/* Section Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 overflow-x-auto no-scrollbar pb-2">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => {
                setActiveSectionId(section.id);
                setActivePageIndex(0);
              }}
              className={`px-6 py-2.5 rounded-xl font-bold text-sm transition-all whitespace-nowrap border-2 ${
                activeSectionId === section.id
                  ? 'bg-black dark:bg-white text-white dark:text-black border-black dark:border-white shadow-lg shadow-black/20 dark:shadow-white/10'
                  : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 border-gray-100 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'
              }`}
            >
              {section.title}
            </button>
          ))}
          {isOwnProfile && (
            <>
              {sections.length > 1 && (
                <button 
                  onClick={() => setShowReorderSections(true)}
                  className="p-2.5 rounded-xl border-2 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white hover:border-black dark:hover:border-white transition-all"
                  title="Reorder Sections"
                >
                  <ArrowsUpDownIcon className="h-5 w-5" />
                </button>
              )}
              <button 
                onClick={() => {
                 // [Subscription removed] Showcase section creation is unrestricted
                  setShowSectionPrompt(true);
                }}
                className="p-2.5 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-800 text-gray-400 dark:text-gray-500 hover:text-black dark:hover:text-white hover:border-black dark:hover:border-white transition-all"
                title="Add New Collection"
              >
                <PlusIcon className="h-5 w-5" />
              </button>
            </>
          )}
        </div>

        {isOwnProfile && activePage && (
          <button 
            onClick={() => setIsEditing(true)}
            className="flex items-center gap-2 px-4 py-2 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400 rounded-xl font-bold text-xs hover:bg-teal-100 dark:hover:bg-teal-900/30 transition-all border border-teal-100 dark:border-teal-800"
          >
            <PencilSquareIcon className="h-4 w-4" />
            Customize Layout
          </button>
        )}
        {isOwnProfile && activeSectionId && (
             <button 
               onClick={() => setShowDeleteSectionModal(true)}
               className="p-2.5 rounded-xl border-2 border-red-100 dark:border-red-900/20 text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-800 transition-all ml-2"
               title="Delete Collection"
             >
               <TrashIcon className="h-5 w-5" />
             </button>
        )}
      </div>

      {/* Page Navigation & Grid */}
      {activeSection && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">{activeSection.title}</h3>
              {activePage?.title && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{activePage.title}</p>
              )}
            </div>
            <div className="flex items-center gap-4">
              {isOwnProfile && (
                <>
                  {activeSection.pages.length > 1 && (
                    <button
                      onClick={() => setShowReorderPages(true)}
                      className="flex items-center gap-2 px-3 py-1.5 text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white transition-all text-xs font-bold border border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 rounded-lg"
                      title="Reorder Pages"
                    >
                      <ArrowsUpDownIcon className="h-4 w-4" />
                      Reorder
                    </button>
                  )}
                  <button
                    onClick={() => setShowPagePrompt(true)}
                    className="flex items-center gap-2 px-3 py-1.5 text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white transition-all text-xs font-bold border border-transparent hover:border-gray-200 dark:hover:border-gray-800 rounded-lg"
                  >
                    <SquaresPlusIcon className="h-4 w-4" />
                    Add Layout
                  </button>
                  <button
                    onClick={() => setShowDeletePageModal(true)}
                    className="flex items-center gap-2 px-3 py-1.5 text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400 transition-all text-xs font-bold border border-transparent hover:border-red-100 dark:hover:border-red-900/20 rounded-lg"
                    title="Delete Layout"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Delete
                  </button>
                </>
              )}
              {activeSection.pages.length > 1 && (
                <div className="flex items-center gap-3 bg-white dark:bg-gray-900 p-1.5 rounded-xl border border-gray-100 dark:border-gray-800 shadow-sm">
                  <button
                    disabled={activePageIndex === 0}
                    onClick={() => setActivePageIndex(prev => prev - 1)}
                    className="p-1.5 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 disabled:opacity-30 hover:bg-black dark:hover:bg-white hover:text-white dark:hover:text-black transition-all"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <span className="text-xs font-bold text-gray-900 dark:text-white px-1">
                    {activePageIndex + 1} / {activeSection.pages.length}
                  </span>
                  <button
                    disabled={activePageIndex === activeSection.pages.length - 1}
                    onClick={() => setActivePageIndex(prev => prev + 1)}
                    className="p-1.5 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 disabled:opacity-30 hover:bg-black dark:hover:bg-white hover:text-white dark:hover:text-black transition-all"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={`${activeSectionId}-${activePageIndex}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              {activePage && (
                <ShowcaseGrid 
                  items={activePage.items} 
                  layoutType={activePage.layout_type}
                  isOwnProfile={isOwnProfile}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      )}

      {/* Layout Editor Modal */}
      {isEditing && activeSection && activePage && (
        <ShowcaseEditor 
          sectionId={activeSection.id}
          page={activePage}
          onClose={() => setIsEditing(false)}
        />
      )}

      {/* Custom Modals */}
      <PromptModal
        isOpen={showSectionPrompt}
        onClose={() => setShowSectionPrompt(false)}
        onConfirm={handleAddSection}
        title="New Collection"
        message="Give your new showcase collection a name (e.g., 'Portrait Series', 'Street Photography')"
        placeholder="Collection title..."
      />

      <PromptModal
        isOpen={showPagePrompt}
        onClose={() => setShowPagePrompt(false)}
        onConfirm={handleAddPage}
        title="New Layout"
        message="Add a new layout page to this collection."
        placeholder="Layout title..."
      />

      <ConfirmationModal
        isOpen={showDeleteSectionModal}
        onClose={() => setShowDeleteSectionModal(false)}
        onConfirm={handleConfirmDeleteSection}
        title="Delete Collection"
        message="Are you sure you want to delete this entire collection? All layouts and items within it will be lost."
        confirmText="Delete Collection"
        type="danger"
      />

      <ConfirmationModal
        isOpen={showDeletePageModal}
        onClose={() => setShowDeletePageModal(false)}
        onConfirm={handleConfirmDeletePage}
        title="Delete Layout"
        message="Are you sure you want to delete this layout page? All items within it will be lost."
        confirmText="Delete Layout"
        type="danger"
      />

      {/* Reorder Modals */}
      <ReorderModal
        isOpen={showReorderSections}
        onClose={() => setShowReorderSections(false)}
        items={sections}
        type="sections"
        onReorder={(reorderedSections) => {
          reorderedSections.forEach((section, index) => {
            dispatch(updateSectionAsync({ ...section, order: index }));
          });
        }}
        onRename={(section, newTitle) => {
          dispatch(updateSectionAsync({ ...section, title: newTitle }));
        }}
      />

      {activeSection && (
        <ReorderModal
          isOpen={showReorderPages}
          onClose={() => setShowReorderPages(false)}
          items={activeSection.pages}
          type="pages"
          onReorder={(reorderedPages) => {
            reorderedPages.forEach((page, index) => {
              dispatch(updatePageAsync({ sectionId: activeSectionId!, page: { ...page, order: index } }));
            });
          }}
          onRename={(page, newTitle) => {
            dispatch(updatePageAsync({ sectionId: activeSectionId!, page: { ...page, title: newTitle } }));
          }}
          getThumbnail={(page) => {
            // Get the first item's image as thumbnail
            const firstItem = page.items?.[0];
            if (!firstItem) return null;
            return getShowcaseImageUrl(firstItem.thumbnail_url || firstItem.image_url);
          }}
        />
      )}

      {/* [Subscription removed] UpgradePopup removed */}
    </div>
  );
};

export default ShowcaseTab;
